import type { SubmitEvent } from "react";

import { useTranslation } from "react-i18next";

import { useCreateEventNote, useUpdateEventNote } from "@/api/bale";
import { isValidationErrorDetail } from "@/compat";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { RichTextarea } from "@/components/RichTextarea";
import { uuidVersion } from "@/fp/uuidVersion";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";
import { useEventWidgets } from "@/pages/EventPage/hooks/useEventWidgets";

import type { NoteWidget } from "./NoteCard";

export function EditingNoteCard({
  widget,
  onSave,
  onCancel,
}: {
  widget: NoteWidget;
  onSave: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { replaceWidget, removeWidget } = useEventWidgets();
  const { data: eventId } = useCurrentEventSuspense({ query: { select: (data) => data.data.event.id } });

  const updateNote = useUpdateEventNote({
    mutation: {
      onSuccess: ({ data: { event_note } }) => {
        replaceWidget(widget.id, { ...widget, ...event_note });
        onSave();
      },
    },
  });

  const createNote = useCreateEventNote({
    mutation: {
      onSuccess: ({ data: { event_note } }) => {
        replaceWidget(widget.id, event_note);
        onSave();
      },
    },
  });

  function handleSubmit(submitEvent: SubmitEvent<HTMLFormElement>) {
    const formData = new FormData(submitEvent.currentTarget);
    const title = formData.get("title")! as string;
    const content = formData.get("content")! as string;
    if (uuidVersion(widget.id) === 7) {
      updateNote.mutate({ widgetId: widget.id, data: { content, title } });
    } else {
      createNote.mutate({ eventId, data: { content, title } });
    }
  }

  return (
    <Card>
      <Form error={updateNote.error ?? createNote.error} onSubmit={handleSubmit}>
        <DragHandle>
          <CardActionsHeader title={t("event-note")}>
            <IconButton
              type="button"
              name="trash"
              className="text-danger"
              disabled={updateNote.isPending || createNote.isPending}
              onClick={() => removeWidget(widget.id)}
            />
            {!!onCancel && (
              <IconButton
                type="button"
                name="x"
                aria-label={t("cancel")}
                disabled={updateNote.isPending || createNote.isPending}
                onClick={onCancel}
              />
            )}
            <IconButton
              type="submit"
              name="save"
              aria-label={t("save")}
              disabled={updateNote.isPending || createNote.isPending}
            />
          </CardActionsHeader>
        </DragHandle>
        <CardTextContent className="flex flex-col gap-4">
          {!!updateNote.error?.info && !isValidationErrorDetail(updateNote.error.info) && (
            <ErrorDetail error={updateNote.error.info} />
          )}
          {!!createNote.error?.info && !isValidationErrorDetail(createNote.error.info) && (
            <ErrorDetail error={createNote.error.info} />
          )}
          <FormField title={t("event-note-title")}>
            <Input
              type="text"
              name="title"
              defaultValue={widget.title ?? ""}
              placeholder={t("event-note-title-placeholder")}
              disabled={updateNote.isPending || createNote.isPending}
            />
          </FormField>
          <FormField title={t("event-note-content")}>
            <RichTextarea
              name="content"
              defaultValue={widget.content?.raw ?? ""}
              placeholder={t("event-note-content-placeholder")}
              disabled={updateNote.isPending || createNote.isPending}
            />
          </FormField>
        </CardTextContent>
      </Form>
    </Card>
  );
}
